import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useWallet } from "@solana/wallet-adapter-react";
import { Connection, Keypair, Transaction, clusterApiUrl } from "@solana/web3.js";
import bs58 from "bs58";
import {
  RegisterAgentRequestSchema,
  type Framework,
  type RegisterAgentRequest,
} from "@altheia-xyz/types";
import { AppChrome } from "@/components/Chrome";
import { Sigil, Hallmark } from "@/components/Sigil";
import { SOLSCAN } from "@/components/Primitives";
import { useAuth } from "@/components/providers/AuthProvider";
import { api, type RegisterAgentApiResponse } from "@/lib/api";

const FRAMEWORKS: { id: Framework; label: string; note: string; status: "shipping" | "planned" }[] = [
  { id: "sak", label: "Solana Agent Kit", note: "sak", status: "shipping" },
  { id: "mcp", label: "MCP", note: "model context", status: "shipping" },
  { id: "custom", label: "Custom", note: "use the SDK directly", status: "shipping" },
  { id: "eliza", label: "ElizaOS", note: "ai16z", status: "planned" },
  { id: "virtuals", label: "Virtuals", note: "virtuals protocol", status: "planned" },
  { id: "griffain", label: "Griffain", note: "griffain.com", status: "planned" },
];

// devnet devUSDC (Orca). When flipping to mainnet, change to EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v.
const USDC = "BRjpCHtyQLNCo8gqRUr8jtdAj5AjPYQaoqbvcZiHok1k";
const SOL = "So11111111111111111111111111111111111111112";
const ENDPOINT = import.meta.env.VITE_SOLANA_RPC_URL ?? clusterApiUrl("devnet");

function clusterLabel(rpc: string): string {
  if (rpc.includes("mainnet")) return "mainnet-beta";
  if (rpc.includes("devnet")) return "devnet";
  if (rpc.includes("testnet")) return "testnet";
  if (rpc.includes("localhost") || rpc.includes("127.0.0.1")) return "localhost";
  return "custom";
}

// Curated catalog of well-known Solana programs. Categories let the chip
// catalog filter as it grows. Mainnet program IDs (same on devnet). Operators
// can also paste a custom pubkey via the "Add custom" input. Each chip carries
// a Solscan ↗ so the operator can verify the ID matches the protocol they
// think they're scoping — no blind trust in our list.
type ProgramCategory = "swap" | "perps" | "lend" | "token";
const PROGRAM_CATALOG: { id: string; name: string; tag: string; category: ProgramCategory; comingSoon?: boolean }[] = [
  { id: "JUP6LkbZbjS1jKKwapdHNy74zcZ3tLUZoi5QNyVTaV4", name: "Jupiter Aggregator v6", tag: "SWAP", category: "swap", comingSoon: true },
  { id: "675kPX9MHTjS2zt1qfr1NYHuzeLXfQM9H24wFSUt1Mp8", name: "Raydium AMM v4", tag: "SWAP", category: "swap", comingSoon: true },
  { id: "whirLbMiicVdio4qvUfM5KAg6Ct8VwpYzGff3uctyCc", name: "Orca Whirlpools", tag: "SWAP", category: "swap" },
  { id: "dRiftyHA39MWEi3m9aunc5MzRF1JYuBsbn6VPcn33UH", name: "Drift Protocol v2", tag: "PERPS", category: "perps", comingSoon: true },
  { id: "KLend2g3cP87fffoy8q1mQqGKjrxjC8boSyAYavgmjD", name: "Kamino Lend", tag: "LEND", category: "lend", comingSoon: true },
  { id: "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA", name: "SPL Token Program", tag: "TOKEN", category: "token" },
];

const CATEGORY_FILTERS: { id: "all" | ProgramCategory; label: string }[] = [
  { id: "all", label: "All" },
  { id: "swap", label: "Swap" },
  { id: "perps", label: "Perps" },
  { id: "lend", label: "Lend" },
  { id: "token", label: "Token" },
];

function isValidPubkey(s: string): boolean {
  // Solana base58 pubkeys are 32–44 chars in base58.
  if (s.length < 32 || s.length > 44) return false;
  return /^[1-9A-HJ-NP-Za-km-z]+$/.test(s);
}

export function RegisterAgentPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { sendTransaction, signTransaction, publicKey } = useWallet();

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [name, setName] = useState("");
  const [framework, setFramework] = useState<Framework>("custom");
  const [chain, setChain] = useState<"solana" | "sui">("solana");
  // USDC caps (smallest unit kept human-readable; operator types whole tokens
  // and we don't multiply for now — backend stores the literal value passed).
  const [usdcMaxPerTx, setUsdcMaxPerTx] = useState("100");
  const [usdcMaxPerDay, setUsdcMaxPerDay] = useState("500");
  const [solMaxPerTx, setSolMaxPerTx] = useState("");
  const [solMaxPerDay, setSolMaxPerDay] = useState("");
  // Allowed programs: store a Set of selected program IDs. Operator can
  // toggle catalog entries or paste a custom one.
  const [allowedPrograms, setAllowedPrograms] = useState<Set<string>>(new Set());
  const [programFilter, setProgramFilter] = useState<"all" | ProgramCategory>("all");
  const [customProgram, setCustomProgram] = useState("");
  const [blockedDest, setBlockedDest] = useState("");
  const [blockedDestList, setBlockedDestList] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitState, setSubmitState] = useState<"idle" | "building" | "swig" | "signing" | "confirming" | "done">("idle");

  // After registration, two secrets need to be shown to the operator ONCE:
  //   - the session-key secret (operator generates client-side, we never see)
  //   - the apiKey cleartext (backend mints, we only keep the hash)
  // Modal gates dashboard navigation until BOTH are copied + acknowledged.
  const [pendingSecret, setPendingSecret] = useState<{
    sessionKeyBase58: string;
    sessionKeyPubkey: string;
    apiKey: string;
    agentId: string;
    agentPda: string;
  } | null>(null);
  const [sessionCopied, setSessionCopied] = useState(false);
  const [apiKeyCopied, setApiKeyCopied] = useState(false);
  const [secretAcked, setSecretAcked] = useState(false);

  const togglePrograms = (id: string) => {
    setAllowedPrograms((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const addCustomProgram = () => {
    const trimmed = customProgram.trim();
    if (!trimmed) return;
    if (!isValidPubkey(trimmed)) {
      setError(`"${trimmed}" doesn't look like a valid Solana pubkey`);
      return;
    }
    setError(null);
    setAllowedPrograms((prev) => new Set(prev).add(trimmed));
    setCustomProgram("");
  };
  const addBlockedDest = () => {
    const trimmed = blockedDest.trim();
    if (!trimmed) return;
    if (!isValidPubkey(trimmed)) {
      setError(`"${trimmed}" doesn't look like a valid Solana pubkey`);
      return;
    }
    setError(null);
    setBlockedDestList((prev) => Array.from(new Set([...prev, trimmed])));
    setBlockedDest("");
  };

  const next = () => setStep((s) => (s < 3 ? ((s + 1) as 1 | 2 | 3) : s));
  const back = () => setStep((s) => (s > 1 ? ((s - 1) as 1 | 2 | 3) : s));

  const submit = async () => {
    if (!token || !publicKey) {
      setError("not signed in");
      return;
    }
    setError(null);
    setSubmitState("building");

    try {
      // Build the asset_caps map from whichever USDC/SOL caps the operator
      // actually set. Empty fields are dropped so the on-chain Vec<TokenCap>
      // only has caps for assets the operator actually constrained.
      const assetCaps: Record<string, { max_per_tx: number; max_per_day: number }> = {};
      const usdcTx = Number(usdcMaxPerTx);
      const usdcDay = Number(usdcMaxPerDay);
      if (Number.isFinite(usdcTx) && usdcTx > 0) {
        assetCaps[USDC] = {
          max_per_tx: usdcTx,
          max_per_day: Number.isFinite(usdcDay) && usdcDay > 0 ? usdcDay : usdcTx,
        };
      }
      const solTx = Number(solMaxPerTx);
      const solDay = Number(solMaxPerDay);
      if (Number.isFinite(solTx) && solTx > 0) {
        assetCaps[SOL] = {
          max_per_tx: solTx,
          max_per_day: Number.isFinite(solDay) && solDay > 0 ? solDay : solTx,
        };
      }

      const policy = {
        asset_caps: assetCaps,
        allowed_programs: Array.from(allowedPrograms),
        blocked_destinations: blockedDestList,
      };

      // SESSION-KEY CUSTODY: generate the agent's session-key keypair right
      // here in the browser. Backend never sees the secret. The pubkey gets
      // registered as a Swig authority; the operator stores the secret
      // (one-time dialog after the tx confirms — see pendingSecret).
      const sessionKey = Keypair.generate();
      const sessionKeyBase58 = bs58.encode(sessionKey.secretKey);

      const req: RegisterAgentRequest = RegisterAgentRequestSchema.parse({
        name,
        framework,
        model_commitment: "0".repeat(64),
        policy,
        session_key_pubkey: sessionKey.publicKey.toBase58(),
      });
      const connection = new Connection(ENDPOINT, "confirmed");

      let result = await api.registerAgent(req, token);

      // First-time Swig: sign + submit + confirm createSwig, then re-call register.
      if ("status" in result && result.status === "create_swig_first") {
        setSubmitState("swig");
        const swigTx = Transaction.from(
          Uint8Array.from(atob(result.transaction.base64), (c) => c.charCodeAt(0)),
        );
        const swigSigned = signTransaction ? await signTransaction(swigTx) : null;
        const swigSig = swigSigned
          ? await connection.sendRawTransaction(swigSigned.serialize())
          : await sendTransaction(swigTx, connection);
        await connection.confirmTransaction(swigSig, "confirmed");

        // Re-call to get the real register tx.
        setSubmitState("building");
        result = await api.registerAgent(req, token);
        if ("status" in result && result.status === "create_swig_first") {
          throw new Error(
            "Backend still reports Swig missing after createSwig confirmation. Check Helius RPC propagation.",
          );
        }
      }

      const fullResult = result as RegisterAgentApiResponse;

      setSubmitState("signing");
      const txBytes = Uint8Array.from(atob(fullResult.transaction.base64), (c) => c.charCodeAt(0));
      const tx = Transaction.from(txBytes);
      const signed = signTransaction ? await signTransaction(tx) : null;
      const signature = signed
        ? await connection.sendRawTransaction(signed.serialize())
        : await sendTransaction(tx, connection);

      setSubmitState("confirming");
      await connection.confirmTransaction(signature, "confirmed");
      await api.confirmAgent(fullResult.agent.id, signature, token);

      setSubmitState("done");
      // Don't navigate yet — show the operator BOTH secrets ONCE:
      //   - sessionKeyBase58: backend never had this; ours-or-gone-forever
      //   - apiKey: backend kept only the hash; same one-shot rule
      // Modal blocks navigation until both are copied + acknowledged.
      setPendingSecret({
        sessionKeyBase58,
        sessionKeyPubkey: sessionKey.publicKey.toBase58(),
        apiKey: fullResult.api_key.cleartext,
        agentId: fullResult.agent.id,
        agentPda: fullResult.agent.on_chain_pubkey,
      });
    } catch (e) {
      setSubmitState("idle");
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const proceedAfterSecret = () => {
    if (!pendingSecret) return;
    const ref = pendingSecret.agentPda || pendingSecret.agentId;
    setPendingSecret(null);
    navigate(`/agents/${ref}`);
  };

  const previewSeed = name || "preview-agent";

  return (
    <AppChrome>
      <div className="page page--register">
        <Link to="/dashboard" className="back-link">← Agents</Link>

        <div className="register-shell">
          <div className="register-progress">
            <span className={`step ${step >= 1 ? "step--active" : ""}`}>
              <span className="step__num num">1</span> Identity
            </span>
            <span className={`step ${step >= 2 ? "step--active" : ""}`}>
              <span className="step__num num">2</span> Policy
            </span>
            <span className={`step ${step >= 3 ? "step--active" : ""}`}>
              <span className="step__num num">3</span> Confirm
            </span>
          </div>

          <div className="register-grid">
            <div className="register-form">
              {step === 1 && (
                <>
                  <span className="kicker">Identity</span>
                  <h1 className="register-form__title">Register a new agent</h1>
                  <p className="register-form__sub">
                    Every agent gets a deterministic sigil derived from its on-chain identity. Same identity, same seal.
                  </p>
                  <label className="form-field">
                    <span className="kicker">Agent name</span>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="agent-trader-1"
                    />
                  </label>
                  <div className="form-field">
                    <span className="kicker">Chain</span>
                    <div className="framework-grid" role="radiogroup" aria-label="Chain">
                      <button
                        type="button"
                        role="radio"
                        aria-checked={chain === "solana"}
                        className={`framework-chip ${chain === "solana" ? "framework-chip--active" : ""}`}
                        onClick={() => setChain("solana")}
                      >
                        <span className="framework-chip__name">Solana</span>
                        <span className="framework-chip__note">Swig session key · Phantom signs</span>
                      </button>
                      <button
                        type="button"
                        role="radio"
                        aria-checked={chain === "sui"}
                        className={`framework-chip ${chain === "sui" ? "framework-chip--active" : ""}`}
                        onClick={() => setChain("sui")}
                      >
                        <span className="framework-chip__name">Sui</span>
                        <span className="framework-chip__note">Move policy object · CLI provisioning</span>
                      </button>
                    </div>
                  </div>
                  <details className="register-agent__advanced">
                    <summary className="register-agent__advanced-summary">Advanced</summary>
                    <div className="form-field">
                      <span className="kicker">Framework</span>
                      <div className="framework-grid" role="radiogroup" aria-label="Framework">
                        {FRAMEWORKS.map((f) => {
                          const planned = f.status === "planned";
                          const active = framework === f.id;
                          return (
                            <button
                              key={f.id}
                              type="button"
                              role="radio"
                              aria-checked={active}
                              aria-disabled={planned}
                              disabled={planned}
                              className={`framework-chip ${active ? "framework-chip--active" : ""} ${planned ? "framework-chip--planned" : ""}`}
                              onClick={() => !planned && setFramework(f.id)}
                            >
                              <span className="framework-chip__name">{f.label}</span>
                              <span className="framework-chip__note">{f.note}</span>
                              {planned && <span className="framework-chip__tag">PLANNED</span>}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </details>
                  <div className="register-form__actions">
                    <Link to="/dashboard" className="btn btn--ghost">Cancel</Link>
                    <button className="btn btn--primary" onClick={next} disabled={!name.trim()}>
                      Next: Policy →
                    </button>
                  </div>
                </>
              )}

              {step === 2 && (
                <>
                  <span className="kicker">Policy</span>
                  <h1 className="register-form__title">Bound the agent on-chain</h1>
                  <p className="register-form__sub">
                    Every field below is stored literally on the AgentAccount PDA. The chain — not our backend — is the floor.
                  </p>

                  {/* Asset caps — operator picks USDC and/or SOL */}
                  <div className="form-section">
                    <span className="kicker kicker--bronze">⊙ Per-asset caps</span>
                    <p className="form-section__sub">
                      Translates to Swig <code>TokenRecurringLimit</code>. Per-day defaults to per-tx if left blank.
                    </p>
                    <div className="form-row form-row--two">
                      <label className="form-field">
                        <span className="kicker">USDC max per tx</span>
                        <input type="number" min="0" value={usdcMaxPerTx} onChange={(e) => setUsdcMaxPerTx(e.target.value)} placeholder="100" />
                      </label>
                      <label className="form-field">
                        <span className="kicker">USDC max per day</span>
                        <input type="number" min="0" value={usdcMaxPerDay} onChange={(e) => setUsdcMaxPerDay(e.target.value)} placeholder="500" />
                      </label>
                    </div>
                    <div className="form-row form-row--two">
                      <label className="form-field">
                        <span className="kicker">SOL max per tx</span>
                        <input type="number" min="0" step="0.1" value={solMaxPerTx} onChange={(e) => setSolMaxPerTx(e.target.value)} placeholder="optional" />
                      </label>
                      <label className="form-field">
                        <span className="kicker">SOL max per day</span>
                        <input type="number" min="0" step="0.1" value={solMaxPerDay} onChange={(e) => setSolMaxPerDay(e.target.value)} placeholder="optional" />
                      </label>
                    </div>
                    <p className="form-section__sub" style={{ marginTop: "var(--s-2)" }}>
                      Note: "per day" is enforced as ~216,000 Solana slots — roughly 24 hours at the network's average slot time. Real wall-clock duration drifts ±2-3 hours depending on slot speed.
                    </p>
                  </div>

                  {/* Allowed programs — chip catalog + custom paste */}
                  <div className="form-section">
                    <span className="kicker kicker--bronze">⊙ Allowed programs</span>
                    <p className="form-section__sub">
                      The agent can only invoke programs in this list. Empty = every program allowed (wildcard). Each entry translates to one Swig <code>Program</code> permission attached to the session authority. Verify any program ID via Solscan ↗ before scoping.
                    </p>
                    <div className="program-filters">
                      {CATEGORY_FILTERS.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          className={`program-filter ${programFilter === c.id ? "program-filter--active" : ""}`}
                          onClick={() => setProgramFilter(c.id)}
                        >
                          {c.label}
                        </button>
                      ))}
                    </div>
                    <div className="program-grid">
                      {PROGRAM_CATALOG.filter((p) => programFilter === "all" || p.category === programFilter).map((p) => {
                        const active = allowedPrograms.has(p.id);
                        const comingSoon = p.comingSoon === true;
                        return (
                          <div
                            key={p.id}
                            className={`program-chip ${active ? "program-chip--active" : ""} ${comingSoon ? "program-chip--coming-soon" : ""}`}
                            title={comingSoon ? "Devnet liquidity unavailable. Mainnet roadmap." : undefined}
                          >
                            <button
                              type="button"
                              className="program-chip__select"
                              onClick={() => {
                                if (comingSoon) return;
                                togglePrograms(p.id);
                              }}
                              aria-pressed={active}
                              aria-disabled={comingSoon}
                              aria-label={`${active ? "Remove" : "Add"} ${p.name}`}
                            >
                              <span className="program-chip__name">{p.name}</span>
                              <span className="program-chip__tag">{p.tag}</span>
                              <span className="program-chip__id">{p.id.slice(0, 6)}…{p.id.slice(-4)}</span>
                            </button>
                            <a
                              href={SOLSCAN(p.id)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="program-chip__verify"
                              title={`Verify ${p.name} on Solscan`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              ↗
                            </a>
                            {comingSoon && <span className="program-chip__badge">Coming soon</span>}
                          </div>
                        );
                      })}
                    </div>
                    <div className="form-row form-row--add">
                      <input
                        type="text"
                        placeholder="Paste a custom program ID (base58 pubkey)"
                        value={customProgram}
                        onChange={(e) => setCustomProgram(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomProgram(); } }}
                      />
                      <button type="button" className="btn btn--ghost" onClick={addCustomProgram} disabled={!customProgram.trim()}>+ Add custom</button>
                    </div>
                    {/* Custom-added programs (not in catalog) shown as removable chips */}
                    {Array.from(allowedPrograms).filter((id) => !PROGRAM_CATALOG.some((p) => p.id === id)).length > 0 && (
                      <ul className="custom-list">
                        {Array.from(allowedPrograms).filter((id) => !PROGRAM_CATALOG.some((p) => p.id === id)).map((id) => (
                          <li key={id} className="custom-chip">
                            <span className="num">{id.slice(0, 8)}…{id.slice(-4)}</span>
                            <button type="button" onClick={() => togglePrograms(id)} className="custom-chip__remove">×</button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* Blocked destinations */}
                  <div className="form-section">
                    <span className="kicker kicker--bronze">⊙ Blocked destinations</span>
                    <p className="form-section__sub">
                      Token transfers to these addresses revert on chain regardless of caps.
                    </p>
                    <div className="form-row form-row--add">
                      <input
                        type="text"
                        placeholder="Paste a destination pubkey to block"
                        value={blockedDest}
                        onChange={(e) => setBlockedDest(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addBlockedDest(); } }}
                      />
                      <button type="button" className="btn btn--ghost" onClick={addBlockedDest} disabled={!blockedDest.trim()}>+ Block</button>
                    </div>
                    {blockedDestList.length > 0 && (
                      <ul className="custom-list">
                        {blockedDestList.map((id) => (
                          <li key={id} className="custom-chip custom-chip--block">
                            <span className="num">{id.slice(0, 8)}…{id.slice(-4)}</span>
                            <button type="button" onClick={() => setBlockedDestList((prev) => prev.filter((x) => x !== id))} className="custom-chip__remove">×</button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {error && <div className="alert">{error}</div>}

                  <div className="register-form__actions">
                    <button className="btn btn--ghost" onClick={back}>← Back</button>
                    <button className="btn btn--primary" onClick={next}>Next: Confirm →</button>
                  </div>
                </>
              )}

              {step === 3 && (
                <>
                  <span className="kicker">Confirm</span>
                  <h1 className="register-form__title">Sign and inscribe</h1>
                  <p className="register-form__sub">
                    {chain === "solana"
                      ? "Phantom will sign one transaction that (a) provisions a Swig session key with the policy above, (b) registers the agent in the Identity Program. Both inscribe in the same block."
                      : "Sui agents provision via the altheia-sui CLI for this submission cycle. Run the snippet below to publish the Move policy + AgentCap; the dashboard ingests them via the Sui event poller and the row lands here automatically."}
                  </p>
                  <div className="confirm-summary">
                    <SummaryRow label="Agent" value={name} />
                    <SummaryRow label="Chain" value={chain} />
                    <SummaryRow label="Framework" value={frameworkLabel(framework)} />
                    {usdcMaxPerTx && Number(usdcMaxPerTx) > 0 && (
                      <SummaryRow label="USDC cap" value={`${usdcMaxPerTx} per tx · ${usdcMaxPerDay || usdcMaxPerTx} per day`} />
                    )}
                    {solMaxPerTx && Number(solMaxPerTx) > 0 && (
                      <SummaryRow label="SOL cap" value={`${solMaxPerTx} per tx · ${solMaxPerDay || solMaxPerTx} per day`} />
                    )}
                    <SummaryRow
                      label="Allowed programs"
                      value={allowedPrograms.size === 0 ? "every program (wildcard)" : `${allowedPrograms.size} scoped`}
                    />
                    <SummaryRow
                      label="Blocked destinations"
                      value={blockedDestList.length === 0 ? "none" : `${blockedDestList.length}`}
                    />
                    <SummaryRow label="Substrate" value={chain === "sui" ? "Move policy object" : "Swig"} />
                    <SummaryRow label="Cluster" value={chain === "sui" ? "Sui testnet" : clusterLabel(ENDPOINT)} />
                  </div>
                  {chain === "sui" && (
                    <pre style={{
                      font: "10px/1.5 var(--font-mono, 'JetBrains Mono', Menlo, monospace)",
                      background: "rgba(11,11,11,0.04)",
                      border: "0.5px solid rgba(11,11,11,0.12)",
                      padding: "10px 12px",
                      marginTop: 12,
                      overflowX: "auto",
                    }}>
{`# 1. clone + build
git clone https://github.com/altheia-xyz/altheia-sui && cd altheia-sui
sui move build && sui move test

# 2. publish to testnet
sui client publish --gas-budget 100000000

# 3. provision vault + mint policy + mint AgentCap
sui client call --package <PKG_ID> --module vault --function provision \\
  --type-args 0x2::sui::SUI --gas-budget 10000000`}
                    </pre>
                  )}
                  {error && <div className="alert">{error}</div>}
                  <div className="register-form__actions">
                    <button className="btn btn--ghost" onClick={back} disabled={submitState !== "idle"}>← Back</button>
                    {chain === "sui" ? (
                      <a
                        className="btn btn--primary"
                        href="https://github.com/altheia-xyz/altheia-sui"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open altheia-sui CLI ↗
                      </a>
                    ) : (
                    <button
                      className="btn btn--primary"
                      onClick={submit}
                      disabled={submitState !== "idle"}
                    >
                      {submitState === "idle" && "Sign with Phantom →"}
                      {submitState === "building" && "Building tx…"}
                      {submitState === "swig" && "Creating Swig smart-account…"}
                      {submitState === "signing" && "Signing register…"}
                      {submitState === "confirming" && "Confirming on-chain…"}
                      {submitState === "done" && "Done"}
                    </button>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="register-preview">
              <span className="kicker">Sigil preview</span>
              <div className="register-preview__frame">
                <Sigil seed={previewSeed} size={180} tone="bone" sweeping strokeWidth={1.1} />
              </div>
              <div className="register-preview__name">{name || "agent-name"}</div>
              <div className="register-preview__sub">{frameworkLabel(framework)}</div>
              <p className="register-preview__hint">
                The sigil is a deterministic seal — the same agent identity always produces the same sigil. It will appear next to every action this agent takes.
              </p>
            </div>
          </div>
        </div>

        <RegisterStyles />
      </div>

      {pendingSecret && (
        <SessionKeyRevealModal
          secret={pendingSecret}
          sessionCopied={sessionCopied}
          apiKeyCopied={apiKeyCopied}
          acked={secretAcked}
          onCopySession={async () => {
            try { await navigator.clipboard.writeText(pendingSecret.sessionKeyBase58); } catch { /* ignore */ }
            setSessionCopied(true);
          }}
          onCopyApiKey={async () => {
            try { await navigator.clipboard.writeText(pendingSecret.apiKey); } catch { /* ignore */ }
            setApiKeyCopied(true);
          }}
          onAck={() => setSecretAcked((v) => !v)}
          onProceed={proceedAfterSecret}
        />
      )}
    </AppChrome>
  );
}

/**
 * Shown ONCE after a successful registration. Displays BOTH:
 *   - the session-key secret (backend never had it — Altheia generated client-side)
 *   - the apiKey cleartext (backend keeps only the hash)
 *
 * Operator must copy each + tick "I've saved both" before navigating.
 */
function SessionKeyRevealModal({
  secret,
  sessionCopied,
  apiKeyCopied,
  acked,
  onCopySession,
  onCopyApiKey,
  onAck,
  onProceed,
}: {
  secret: {
    sessionKeyBase58: string;
    sessionKeyPubkey: string;
    apiKey: string;
    agentId: string;
    agentPda: string;
  };
  sessionCopied: boolean;
  apiKeyCopied: boolean;
  acked: boolean;
  onCopySession: () => void;
  onCopyApiKey: () => void;
  onAck: () => void;
  onProceed: () => void;
}) {
  const sdkSnippet = `import { Altheia } from "@altheia-xyz/sdk";\nimport { Keypair } from "@solana/web3.js";\nimport bs58 from "bs58";\n\nconst sessionKey = Keypair.fromSecretKey(\n  bs58.decode(process.env.ALTHEIA_SESSION_KEY!),\n);\n\nconst altheia = new Altheia({\n  agentPda: "${secret.agentPda}",\n  apiKey: process.env.ALTHEIA_API_KEY!,\n});`;

  return (
    <div className="reveal-backdrop">
      <div className="reveal-card">
        <div className="reveal-card__head">
          <Hallmark size={11} tone="bronze" />
          <span className="kicker kicker--bronze">Shown once · cannot be recovered</span>
        </div>
        <h2 className="reveal-card__title">Save both secrets now</h2>

        <div className="reveal-row">
          <span className="kicker">Session-key pubkey · safe to share</span>
          <code className="reveal-mono">{secret.sessionKeyPubkey}</code>
        </div>

        <div className="reveal-row reveal-row--secret">
          <span className="kicker">ALTHEIA_SESSION_KEY · base58 · never sent to us</span>
          <code className="reveal-mono reveal-mono--secret">{secret.sessionKeyBase58}</code>
          <button type="button" className="btn btn--ghost reveal-copy" onClick={onCopySession}>
            {sessionCopied ? "Copied ✓" : "Copy"}
          </button>
        </div>

        <div className="reveal-row reveal-row--secret">
          <span className="kicker">ALTHEIA_API_KEY · hash retained server-side</span>
          <code className="reveal-mono reveal-mono--secret">{secret.apiKey}</code>
          <button type="button" className="btn btn--ghost reveal-copy" onClick={onCopyApiKey}>
            {apiKeyCopied ? "Copied ✓" : "Copy"}
          </button>
        </div>

        <details className="reveal-snippet">
          <summary>SDK snippet</summary>
          <pre>{sdkSnippet}</pre>
        </details>

        <label className="reveal-ack">
          <input type="checkbox" checked={acked} onChange={onAck} />
          <span>Saved both</span>
        </label>

        <button
          type="button"
          className="btn btn--primary reveal-proceed"
          onClick={onProceed}
          disabled={!acked || !sessionCopied || !apiKeyCopied}
        >
          Continue →
        </button>

        <RevealStyles />
      </div>
    </div>
  );
}

function RevealStyles() {
  return (
    <style>{`
      .reveal-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.55); display: flex; align-items: center; justify-content: center; z-index: 1100; padding: var(--s-5); }
      .reveal-card { background: var(--bg-elev); border: 1px solid var(--bronze); padding: var(--s-6); width: 640px; max-width: 100%; max-height: 92vh; overflow-y: auto; display: flex; flex-direction: column; gap: var(--s-3); box-shadow: 0 0 0 1px var(--bronze-tint), 0 24px 48px rgba(0,0,0,0.4); }
      .reveal-card__head { display: inline-flex; align-items: center; gap: var(--s-2); }
      .reveal-card__title { font: var(--type-display-section); margin: 0; }
      .reveal-card__body { color: var(--slate); font: var(--type-body); margin: 0; }
      .reveal-card__body strong { color: var(--bone); }
      .reveal-card__body code { font: var(--type-mono-address); color: var(--bronze); background: var(--bronze-tint); padding: 1px 6px; }
      .reveal-row { display: flex; flex-direction: column; gap: var(--s-1); padding: var(--s-3); background: var(--bg); border: 1px solid var(--line-soft); }
      .reveal-row--secret { border-color: var(--bronze); background: var(--bronze-tint); position: relative; }
      .reveal-mono { font: var(--type-mono-address); color: var(--bone); word-break: break-all; line-height: 1.5; }
      .reveal-mono--secret { color: var(--bronze-deep); font-weight: 600; }
      .reveal-copy { align-self: flex-end; margin-top: var(--s-2); padding: var(--s-1) var(--s-3); font-size: 11px; }
      .reveal-snippet summary { cursor: pointer; font: var(--type-kicker); letter-spacing: var(--tracking-kicker); text-transform: uppercase; color: var(--slate); padding: var(--s-2); }
      .reveal-snippet summary:hover { color: var(--bone); }
      .reveal-snippet pre { background: var(--bg); border: 1px solid var(--line-soft); padding: var(--s-3); font: var(--type-mono-address); color: var(--bone); overflow-x: auto; margin: var(--s-2) 0 0; }
      .reveal-ack { display: flex; gap: var(--s-2); align-items: flex-start; padding: var(--s-3); background: var(--bg); border-left: 2px solid var(--oxblood); color: var(--bone); font: var(--type-body-small); cursor: pointer; }
      .reveal-ack input { margin-top: 2px; }
      .reveal-proceed:disabled { opacity: 0.45; cursor: not-allowed; }
      .reveal-proceed { margin-top: var(--s-2); }
    `}</style>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="summary-row">
      <span className="kicker">{label}</span>
      <span>{value}</span>
    </div>
  );
}

function frameworkLabel(f: Framework): string {
  return FRAMEWORKS.find((x) => x.id === f)?.label ?? f;
}

function RegisterStyles() {
  return (
    <style>{`
      .register-shell { padding: var(--s-5) 0; }
      .register-progress { display: flex; gap: var(--s-7); justify-content: center; margin-bottom: var(--s-7); padding-bottom: var(--s-5); border-bottom: 1px solid var(--line-soft); }
      .step { display: inline-flex; align-items: center; gap: var(--s-2); font: var(--type-kicker); letter-spacing: var(--tracking-kicker); text-transform: uppercase; color: var(--slate-2); }
      .step__num { width: 24px; height: 24px; border-radius: 999px; border: 1px solid var(--line); display: inline-flex; align-items: center; justify-content: center; font-size: 11px; }
      .step--active { color: var(--bone); }
      .step--active .step__num { border-color: var(--bronze); background: var(--bronze-tint); color: var(--bronze-deep); }
      .register-grid { display: grid; grid-template-columns: 2fr 1fr; gap: var(--s-7); align-items: start; }
      .register-form { display: flex; flex-direction: column; gap: var(--s-3); }
      .register-form__title { font: var(--type-display-page); font-style: italic; margin: var(--s-2) 0; }
      .register-form__sub { color: var(--slate); font: var(--type-body-large); margin-bottom: var(--s-3); }
      .register-form__sub code { font: var(--type-mono-address); color: var(--bronze); background: var(--bronze-tint); padding: 1px 6px; }
      .form-field { display: flex; flex-direction: column; gap: var(--s-2); margin-bottom: var(--s-3); }
      .form-field input, .form-field select { background: var(--bg); border: 1px solid var(--line); padding: var(--s-3); font: var(--type-body); color: var(--bone); }
      .form-field input:focus, .form-field select:focus { outline: none; border-color: var(--bronze); }

      /* Framework chip grid — replaces the native select dropdown that broke
         design language. Mirrors the landing's lp-integrations__fw chip
         vocabulary + the Approach C 'PLANNED' honesty tag. */
      .framework-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: var(--s-2); margin-top: var(--s-1); }
      .framework-chip {
        position: relative;
        display: flex;
        flex-direction: column;
        gap: 2px;
        background: var(--bg-elev);
        border: 1px solid var(--line);
        padding: var(--s-2) var(--s-3);
        text-align: left;
        cursor: pointer;
        transition: border-color 160ms var(--ease-inscribe), background 160ms var(--ease-inscribe), color 160ms var(--ease-inscribe);
      }
      .framework-chip:hover:not(:disabled) { border-color: var(--bone); }
      .framework-chip__name { font-family: var(--font-display); font-style: italic; font-size: 16px; color: var(--bone); line-height: 1.1; }
      .framework-chip__note { font-family: var(--font-mono); font-size: 9.5px; letter-spacing: 0.16em; text-transform: uppercase; color: var(--slate-2); margin-top: 4px; }
      .framework-chip--active { border-color: var(--bronze); background: var(--bronze-tint); }
      .framework-chip--active .framework-chip__name { color: var(--bronze-deep); }
      .framework-chip--active::before {
        content: '';
        position: absolute;
        top: -1px; left: -1px; width: 6px; height: 6px;
        border-top: 1px solid var(--bronze);
        border-left: 1px solid var(--bronze);
      }
      .framework-chip--active::after {
        content: '';
        position: absolute;
        bottom: -1px; right: -1px; width: 6px; height: 6px;
        border-bottom: 1px solid var(--bronze);
        border-right: 1px solid var(--bronze);
      }
      .framework-chip--planned { opacity: 0.55; cursor: not-allowed; }
      .framework-chip--planned:hover { border-color: var(--line); }
      .framework-chip__tag {
        position: absolute;
        top: 6px; right: 6px;
        font-family: var(--font-mono);
        font-size: 8px;
        letter-spacing: 0.16em;
        color: var(--slate-2);
        border: 1px solid var(--line);
        padding: 1px 4px;
      }
      .form-note { padding: var(--s-3); background: var(--bg-elev); border-left: 2px solid var(--bronze); font: var(--type-body-small); color: var(--slate); display: flex; gap: var(--s-2); align-items: center; flex-wrap: wrap; }

      .register-agent__advanced { margin-top: var(--s-3); border-top: 1px solid var(--line-soft); padding-top: var(--s-3); }
      .register-agent__advanced-summary { color: var(--slate); font-size: 12px; letter-spacing: 0.1em; text-transform: uppercase; cursor: pointer; font-family: var(--font-display); }
      .register-agent__advanced[open] .register-agent__advanced-summary { color: var(--bone); }

      /* Step 2 policy form sections */
      .form-section { display: flex; flex-direction: column; gap: var(--s-2); padding: var(--s-3) var(--s-4); background: var(--bg-elev); border: 1px solid var(--line-soft); margin-bottom: var(--s-3); }
      .form-section__sub { color: var(--slate); font: var(--type-body-small); margin: 0; }
      .form-section__sub code { font: var(--type-mono-address); color: var(--bronze); background: var(--bronze-tint); padding: 1px 6px; }
      .form-row--two { display: grid; grid-template-columns: 1fr 1fr; gap: var(--s-3); margin-bottom: 0; }
      .form-row--two .form-field { margin-bottom: 0; }
      .form-row--add { display: flex; gap: var(--s-2); }
      .form-row--add input { flex: 1; background: var(--bg); border: 1px solid var(--line); padding: var(--s-2) var(--s-3); font: var(--type-mono-address); color: var(--bone); }
      .form-row--add input:focus { outline: none; border-color: var(--bronze); }

      /* Allowed-program category filters */
      .program-filters { display: flex; gap: var(--s-2); flex-wrap: wrap; margin-bottom: var(--s-1); }
      .program-filter { padding: 4px 10px; background: transparent; border: 1px solid var(--line); font-family: var(--font-mono); font-size: 9.5px; letter-spacing: 0.16em; text-transform: uppercase; color: var(--slate-2); cursor: pointer; transition: border-color 160ms, color 160ms, background 160ms; }
      .program-filter:hover { border-color: var(--bone); color: var(--bone); }
      .program-filter--active { border-color: var(--bronze); color: var(--bronze-deep); background: var(--bronze-tint); }

      /* Allowed-program chip catalog (chip = select-button + verify-link side-by-side) */
      .program-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: var(--s-2); }
      .program-chip { position: relative; display: flex; align-items: stretch; background: var(--bg); border: 1px solid var(--line); transition: border-color 160ms var(--ease-inscribe), background 160ms var(--ease-inscribe); }
      .program-chip:hover { border-color: var(--bone); }
      .program-chip__select { flex: 1; display: flex; flex-direction: column; gap: 2px; padding: var(--s-2) var(--s-3); background: transparent; border: 0; text-align: left; cursor: pointer; color: inherit; }
      .program-chip__name { font-family: var(--font-display); font-style: italic; font-size: 13px; color: var(--bone); line-height: 1.1; }
      .program-chip__tag { font-family: var(--font-mono); font-size: 8px; letter-spacing: 0.16em; color: var(--slate-2); margin-top: 2px; }
      .program-chip__id { font-family: var(--font-mono); font-size: 9px; color: var(--slate); margin-top: 3px; }
      .program-chip__verify { display: inline-flex; align-items: center; justify-content: center; width: 28px; border-left: 1px solid var(--line); color: var(--slate-2); font-family: var(--font-mono); font-size: 11px; cursor: pointer; transition: color 160ms, background 160ms; }
      .program-chip__verify:hover { color: var(--bronze); background: var(--bronze-tint); }
      .program-chip--active { border-color: var(--bronze); background: var(--bronze-tint); }
      .program-chip--active .program-chip__name { color: var(--bronze-deep); }
      .program-chip--active .program-chip__verify { border-left-color: var(--bronze); }
      .program-chip--active::before { content: '✓'; position: absolute; top: 4px; right: 36px; color: var(--bronze); font-family: var(--font-mono); font-size: 11px; }
      .program-chip--coming-soon { opacity: 0.45; cursor: not-allowed; position: relative; }
      .program-chip--coming-soon:hover { background: inherit !important; border-color: inherit !important; }
      .program-chip--coming-soon .program-chip__select { cursor: not-allowed; }
      .program-chip__badge {
        position: absolute;
        top: -8px;
        right: -8px;
        background: var(--bronze);
        color: var(--text-on-ink);
        font-size: 9px;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        padding: 2px 6px;
        border-radius: 2px;
        font-family: var(--font-display);
      }

      /* Custom-pubkey list (allowed_programs paste-ins + blocked_destinations) */
      .custom-list { list-style: none; padding: 0; margin: 0; display: flex; flex-wrap: wrap; gap: var(--s-2); }
      .custom-chip { display: inline-flex; align-items: center; gap: var(--s-2); padding: 2px 6px 2px 8px; border: 1px solid var(--line); background: var(--bg); }
      .custom-chip .num { font-size: 11px; color: var(--bone); }
      .custom-chip--block { border-color: var(--oxblood); }
      .custom-chip--block .num { color: var(--oxblood); }
      .custom-chip__remove { background: none; border: 0; color: var(--slate); cursor: pointer; padding: 0 4px; font-size: 14px; line-height: 1; }
      .custom-chip__remove:hover { color: var(--oxblood); }
      .register-form__actions { display: flex; gap: var(--s-3); justify-content: space-between; margin-top: var(--s-5); padding-top: var(--s-5); border-top: 1px solid var(--line-soft); }
      .confirm-summary { background: var(--bg-elev); border: 1px solid var(--line-soft); padding: var(--s-4); display: flex; flex-direction: column; gap: var(--s-3); }
      .summary-row { display: grid; grid-template-columns: 140px 1fr; gap: var(--s-3); align-items: baseline; padding-bottom: var(--s-2); border-bottom: 1px dashed var(--line-soft); }
      .summary-row:last-child { border-bottom: 0; padding-bottom: 0; }
      .summary-row span:last-child { font: var(--type-body); color: var(--bone); }
      .register-preview { position: sticky; top: var(--s-5); background: var(--bg-elev); border: 1px solid var(--line-soft); padding: var(--s-5); text-align: center; display: flex; flex-direction: column; align-items: center; gap: var(--s-2); }
      .register-preview__frame { padding: var(--s-5); }
      .register-preview__name { font: var(--type-display-section); font-style: italic; }
      .register-preview__sub { color: var(--slate); font: var(--type-body-small); }
      .register-preview__hint { color: var(--slate-2); font: var(--type-body-small); margin-top: var(--s-3); padding-top: var(--s-3); border-top: 1px dashed var(--line-soft); text-align: left; }
      @media (max-width: 768px) {
        .register-grid { grid-template-columns: 1fr; }
        .register-progress { gap: var(--s-3); }
      }
    `}</style>
  );
}
